import { getEnv } from './env';
import { authenticatedFetch, getAuthHeaders, hasStoredAuthSession } from '../lib/authApi';
import { isDevelopmentEnv } from './appEnv';

interface EncryptRequest {
  plaintext: string;
  clubId: string;
  teamId?: string;
}

const CRYPTO_REQUEST_TIMEOUT_MS = 8000;
const isCryptoDebugEnabled = (): boolean => isDevelopmentEnv();

const throwIfCryptoResponseNotOk = async (response: Response, fallbackMessage: string): Promise<void> => {
  if (response.ok) return;

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const extractMessage = () => {
    if (payload && typeof payload === 'object') {
      const record = payload as Record<string, unknown>;
      if (typeof record.message === 'string' && record.message.trim().length > 0) return record.message;
      if (typeof record.error === 'string' && record.error.trim().length > 0) return record.error;
      if (typeof record.detail === 'string' && record.detail.trim().length > 0) return record.detail;
      if (record.detail && typeof record.detail === 'object') {
        const detail = record.detail as Record<string, unknown>;
        if (typeof detail.message === 'string' && detail.message.trim().length > 0) return detail.message;
        if (typeof detail.error === 'string' && detail.error.trim().length > 0) return detail.error;
      }
    }
    return fallbackMessage;
  };

  if (response.status === 401) {
    throw new Error('Je bent niet ingelogd of je sessie is verlopen.');
  }
  if (response.status === 403) {
    throw new Error('Je hebt geen rechten om versleutelde gegevens te gebruiken.');
  }

  throw new Error(extractMessage());
};

const getCryptoHeaders = (): HeadersInit => {
  if (!hasStoredAuthSession()) {
    throw new Error('Je bent niet ingelogd of je sessie is verlopen.');
  }

  return getAuthHeaders({
    'Content-Type': 'application/json',
  });
};

const fetchWithTimeout = async (input: string | URL, init: RequestInit = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CRYPTO_REQUEST_TIMEOUT_MS);
  try {
    return await authenticatedFetch(input, { ...init, credentials: 'include', signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Calls the encryption service to encrypt a value for a club (and optional team).
 * Returns the ciphertext string provided by the API.
 */
export const encryptValue = async ({ plaintext, clubId, teamId }: EncryptRequest): Promise<string> => {
  const baseUrl = getEnv('VITE_AI_API_URL', '');

  if (!baseUrl) {
    throw new Error('Encryption service is not configured');
  }

  const payload: Record<string, string> = {
    plaintext,
    club_id: clubId,
  };
  if (teamId) {
    payload.team_id = teamId;
  }

  const response = await fetchWithTimeout(`${baseUrl.replace(/\/$/, '')}/crypto/encrypt`, {
    method: 'POST',
    headers: getCryptoHeaders(),
    body: JSON.stringify(payload),
  });
  await throwIfCryptoResponseNotOk(response, `Encryption failed with status ${response.status}`);

  const data = await response.json();
  const ciphertext =
    data?.ciphertext ||
    data?.encrypted ||
    data?.encryption_key ||
    data?.data ||
    '';

  if (!ciphertext) {
    throw new Error('Encryption service returned no ciphertext');
  }

  return ciphertext;
};

interface DecryptRequest {
  ciphertext: string;
  clubId: string;
}

export const looksEncrypted = (value?: string | null) =>
  typeof value === 'string' && value.startsWith('gAAAA');

const decryptCache = new Map<string, string>();
const pendingDecryptRequests = new Map<string, Promise<string>>();

const getDecryptCacheKey = ({ ciphertext, clubId }: DecryptRequest): string =>
  `${clubId}:${ciphertext}`;

/**
 * Calls the encryption service to decrypt a value for a club.
 * Returns the plaintext string provided by the API.
 */
export const decryptValue = async ({ ciphertext, clubId }: DecryptRequest): Promise<string> => {
  const baseUrl = getEnv('VITE_AI_API_URL', '');

  if (!baseUrl) {
    throw new Error('Encryption service is not configured');
  }

  const cacheKey = getDecryptCacheKey({ ciphertext, clubId });
  const cachedPlaintext = decryptCache.get(cacheKey);
  if (cachedPlaintext) {
    return cachedPlaintext;
  }

  const pendingRequest = pendingDecryptRequests.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const decryptPromise = (async () => {
    const response = await fetchWithTimeout(`${baseUrl.replace(/\/$/, '')}/crypto/decrypt`, {
      method: 'POST',
      headers: getCryptoHeaders(),
      body: JSON.stringify({
        ciphertext,
        club_id: clubId,
      }),
    });
    await throwIfCryptoResponseNotOk(response, `Decryption failed with status ${response.status}`);

    const data = await response.json();
    const plaintext = data?.plaintext || '';

    if (!plaintext) {
      throw new Error('Decryption service returned no plaintext');
    }

    decryptCache.set(cacheKey, plaintext);
    return plaintext;
  })();

  pendingDecryptRequests.set(cacheKey, decryptPromise);
  try {
    return await decryptPromise;
  } finally {
    pendingDecryptRequests.delete(cacheKey);
  }
};

interface DecryptIfEncryptedRequest {
  value?: string | null;
  clubId?: string | null;
}

/**
 * Decrypts when the value looks encrypted; otherwise returns the original value.
 */
export const decryptIfEncrypted = async ({
  value,
  clubId,
}: DecryptIfEncryptedRequest): Promise<string | undefined | null> => {
  if (!value || !clubId || !looksEncrypted(value)) {
    return value;
  }
  try {
    return await decryptValue({ ciphertext: value, clubId });
  } catch (error) {
    if (isCryptoDebugEnabled()) {
      console.warn('[crypto:decryptIfEncrypted:fallback]', {
        clubId,
        ciphertextPreview: `${value.slice(0, 18)}...`,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    return value;
  }
};
